export interface ProductLabelMeta {
  [key: string]: string | number | boolean;
}

export interface ProductLabel {
  code?: string;
  name?: string;
  value?: string;
  source?: string;
  meta?: ProductLabelMeta;
}

export interface ProductAttribute {
  key: string;
  name: string;
  value: string;
}

export interface ProductVariation {
  id: string;
  attributes: Record<string, string>;
  quantity?: number;
  price?: number;
  priceOld?: number;
  name?: string;
  labels?: ProductLabel[];
  picture?: string;
  brand?: string;
  commerce?: any;
}

export interface ProductDetailData {
  id?: string;
  name: string;
  sku?: string;
  code?: string;
  brand?: string;
  rating?: number;
  reviewCount?: number;
  picture?: string;
  price?: number;
  priceOld?: number;
  measure?: string;
  available: boolean;
  descriptionHtml?: string;
  shortDescriptionHtml?: string;
  imageGallery: string[];
  labels: ProductLabel[];
  variations: ProductVariation[];
  displayAttributes: ProductAttribute[];
  allAttributes: ProductAttribute[];
  recommendationSections: string[];
  commerce?: any;
  taxonomy?: any;
  languages?: any;
  media?: any;
}

type RawProduct = any;

const normalizeAttributeValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (entry === null || entry === undefined) {
          return "";
        }
        return typeof entry === "string" ? entry.trim() : String(entry);
      })
      .filter(Boolean)
      .join(", ");
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    return "";
  }

  return typeof value === "string" ? value.trim() : String(value);
};

// Удаляем теги, которые могут приносить дублирующиеся мета-данные из HTML, пришедшего из CMS
const sanitizeHtmlContent = (html?: string): string | undefined => {
  if (typeof html !== "string") {
    return undefined;
  }
  const cleaned = html
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "")
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, "")
    .replace(/<meta[^>]*>/gi, "")
    .trim();

  return cleaned || undefined;
};

const toAttributeList = (
  entries: Array<[string, unknown]>,
  resolve: (payload: unknown, key: string) => ProductAttribute | null
): ProductAttribute[] => {
  const result: ProductAttribute[] = [];
  entries.forEach(([key, payload]) => {
    const attribute = resolve(payload, key);
    if (!attribute) {
      return;
    }
    result.push(attribute);
  });
  return result;
};

const sortEntriesByField = (
  entries: Array<[string, unknown]>,
  fieldName: string
): Array<[string, unknown]> => {
  // Стабильная сортировка по числовому полю внутри payload (например, payload.sort).
  const withMeta = entries.map(([key, payload], idx) => {
    const raw =
      payload && typeof payload === "object" && !Array.isArray(payload)
        ? (payload as any)[fieldName]
        : undefined;
    const num =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
        ? Number(raw)
        : Number.NaN;
    return { key, payload, idx, num };
  });

  const hasAny = withMeta.some((x) => Number.isFinite(x.num));
  if (!hasAny) {
    return entries;
  }

  return withMeta
    .slice()
    .sort((a, b) => {
      const aHas = Number.isFinite(a.num);
      const bHas = Number.isFinite(b.num);
      if (aHas && bHas) {
        if (a.num !== b.num) return a.num - b.num;
        return a.idx - b.idx;
      }
      if (aHas) return -1;
      if (bHas) return 1;
      return a.idx - b.idx;
    })
    .map((x) => [x.key, x.payload] as [string, unknown]);
};

const buildFeaturedAttributes = (product: RawProduct): ProductAttribute[] => {
  const featured = product.featured;
  if (!featured || typeof featured !== "object" || Array.isArray(featured)) {
    return [];
  }
  const sortedEntries = sortEntriesByField(Object.entries(featured), "sort");
  return toAttributeList(sortedEntries, (entry, key) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      const value = normalizeAttributeValue(entry);
      if (!value) {
        return null;
      }
      return {
        key,
        name: key,
        value,
      };
    }
    const typed = entry as Record<string, unknown>;
    const value = normalizeAttributeValue(typed.value ?? typed.name);
    if (!value) {
      return null;
    }
    const name =
      typeof typed.name === "string" && typed.name.trim()
        ? typed.name.trim()
        : key;
    return {
      key,
      name,
      value,
    };
  });
};

const buildPropertyAttributes = (product: RawProduct): ProductAttribute[] => {
  const propertyMap = product.properties;
  if (!propertyMap || typeof propertyMap !== "object" || Array.isArray(propertyMap)) {
    return [];
  }
  const sortedEntries = sortEntriesByField(Object.entries(propertyMap), "sort");
  return toAttributeList(sortedEntries, (entry, key) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return null;
    }
    const typed = entry as Record<string, unknown>;
    const value = normalizeAttributeValue(typed.value);
    if (!value) {
      return null;
    }
    const name =
      typeof typed.name === "string" && typed.name.trim()
        ? typed.name.trim()
        : key;
    return {
      key,
      name,
      value,
    };
  });
};

const dedupeAttributes = (attributes: ProductAttribute[]): ProductAttribute[] => {
  const seen = new Set<string>();
  const result: ProductAttribute[] = [];
  attributes.forEach((attribute) => {
    const hash = `${attribute.key}|${attribute.name}|${attribute.value}`;
    if (seen.has(hash)) {
      return;
    }
    seen.add(hash);
    result.push(attribute);
  });
  return result;
};

const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.replace(/\s+/g, "").replace(",", ".");
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const sanitizeImageList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter(Boolean);
};

const sanitizeLabels = (value: unknown): ProductLabel[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      const typed = entry as Record<string, unknown>;
      const meta =
        typed.meta && typeof typed.meta === "object"
          ? (typed.meta as Record<string, unknown>)
          : undefined;
      const normalizedMeta: ProductLabelMeta | undefined = meta
        ? Object.entries(meta).reduce<ProductLabelMeta>((acc, [metaKey, metaValue]) => {
            if (metaValue === null || metaValue === undefined) {
              return acc;
            }
            if (typeof metaValue === "boolean" || typeof metaValue === "number") {
              acc[metaKey] = metaValue;
              return acc;
            }
            acc[metaKey] = String(metaValue);
            return acc;
          }, {})
        : undefined;

      const valueCandidate =
        typed.value ?? typed.name ?? meta?.name ?? meta?.value;
      const valueString =
        typeof valueCandidate === "string"
          ? valueCandidate.trim()
          : valueCandidate !== undefined && valueCandidate !== null
          ? String(valueCandidate)
          : "";
      if (!valueString) {
        return null;
      }

      return {
        code: typeof typed.code === "string" ? typed.code : undefined,
        name: typeof typed.name === "string" ? typed.name : undefined,
        value: valueString,
        source: typeof typed.source === "string" ? typed.source : undefined,
        meta: normalizedMeta,
      };
    })
    .filter(Boolean) as ProductLabel[];
};

const sanitizeVariations = (value: unknown): ProductVariation[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      const typed = entry as Record<string, unknown>;
      const idCandidate = typed.id ?? typed.code ?? typed.slug;
      if (idCandidate === undefined || idCandidate === null) {
        return null;
      }
      const attributesRaw = typed.attributes;
      const attributes =
        attributesRaw && typeof attributesRaw === "object" && !Array.isArray(attributesRaw)
          ? Object.entries(attributesRaw).reduce<Record<string, string>>((acc, [attrKey, attrValue]) => {
              if (typeof attrValue === "string") {
                acc[attrKey] = attrValue;
              } else if (attrValue !== null && attrValue !== undefined) {
                acc[attrKey] = String(attrValue);
              }
              return acc;
            }, {})
          : {};

      return {
        id: String(idCandidate),
        attributes,
        quantity: parseNumber(typed.quantity),
        price: parseNumber(typed.price),
        priceOld: parseNumber(typed.priceOld ?? typed.price_old),
        name: typeof typed.name === "string" ? typed.name : undefined,
        labels: sanitizeLabels(typed.labels),
        picture: typeof typed.picture === "string" ? typed.picture : undefined,
        brand: typeof typed.brand === "string" ? typed.brand : undefined,
      };
    })
    .filter(Boolean) as ProductVariation[];
};

const deriveRecommendationSections = (product: RawProduct): string[] => {
  const direct = product.sectionCode;
  if (Array.isArray(direct)) {
    return direct
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean);
  }
  if (typeof direct === "string" && direct.trim()) {
    return direct
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }

  if (typeof product.category === "string" && product.category.trim()) {
    return [product.category.trim()];
  }

  return [];
};

const deriveProductBrand = (product: RawProduct): string | undefined => {
  const candidates: unknown[] = [];
  const properties = product.properties as Record<string, unknown> | undefined;
  if (properties?.BREND && typeof properties.BREND === "object") {
    const typed = properties.BREND as Record<string, unknown>;
    candidates.push(typed.value ?? typed.name);
  }

  for (const candidate of candidates) {
    const normalized = normalizeAttributeValue(candidate);
    if (!normalized) {
      continue;
    }
    const trimmed = normalized.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return undefined;
};

const deriveProductCode = (product: RawProduct): string | undefined => {
  if (typeof product.code === "string" && product.code.trim()) {
    return product.code.trim();
  }
  if (typeof product.id === "string" && product.id.trim()) {
    return product.id.trim();
  }
  if (typeof product.id === "number") {
    return String(product.id);
  }
  return undefined;
};

export const prepareProductDetailData = (product: RawProduct, langCode?: string): ProductDetailData => {
  const featured = buildFeaturedAttributes(product);
  const displayAttributes = featured;

  const allAttributes = dedupeAttributes(buildPropertyAttributes(product));

  const idRaw = product.id;
  const id =
    typeof idRaw === "number"
      ? String(idRaw)
      : typeof idRaw === "string"
      ? idRaw
      : undefined;

  // Получаем название из языковых версий, если указан langCode
  let productName = typeof product.name === "string" && product.name.trim()
    ? product.name
    : "Товар";
  
  // Сначала пытаемся получить из языковых версий
  if (langCode) {
    const languages = product.languages;
    if (languages && typeof languages === 'object' && !Array.isArray(languages)) {
      const langData = languages[langCode];
      if (langData && typeof langData === 'object' && !Array.isArray(langData)) {
        const langName = langData.name;
        if (langName && typeof langName === "string" && langName.trim()) {
          productName = langName.trim();
        }
      }
    }
  }
  
  // Получаем описание из языковых версий, если указан langCode
  let descriptionHtml: string | undefined = undefined;
  
  // Сначала пытаемся получить из языковых версий товара
  if (langCode) {
    const languages = product.languages;
    if (languages && typeof languages === 'object' && !Array.isArray(languages)) {
      const langData = languages[langCode];
      if (langData && typeof langData === 'object' && !Array.isArray(langData)) {
        const langDescription = langData.description;
        if (langDescription && typeof langDescription === "string" && langDescription.trim()) {
          descriptionHtml = langDescription.trim();
        }
      }
    }
  }
  
  // Fallback на обычное описание, если нет языкового
  if (!descriptionHtml && typeof product.description === "string" && product.description.trim()) {
    descriptionHtml = product.description.trim();
  }

  // shortDescriptionHtml должен быть передан из проекта, если не передан - используем значение из product
  const rawShortDescription: string | undefined =
    typeof product.shortDescriptionHtml === "string" && product.shortDescriptionHtml.trim()
      ? product.shortDescriptionHtml.trim()
      : undefined;
  const shortDescriptionHtml = sanitizeHtmlContent(rawShortDescription);

  // Извлекаем изображения из product.image_gallery
  const imageGallerySource = product.image_gallery ?? [];

  // Извлекаем основное изображение из product.picture
  const pictureSource =
    (typeof product.picture === "string" && product.picture.trim()) 
      ? product.picture 
      : undefined;
  const sanitizedDescriptionHtml = sanitizeHtmlContent(descriptionHtml);

  return {
    id,
    name: productName,
    sku: typeof product.sku === "string" ? product.sku : undefined,
    code: deriveProductCode(product),
    brand: deriveProductBrand(product),
    rating: typeof product.rating === "number" ? product.rating : undefined,
    reviewCount:
      typeof product.reviewCount === "number" ? product.reviewCount : undefined,
    picture: pictureSource,
    price: parseNumber(product.price),
    priceOld: parseNumber(product.priceOld ?? product.price_old),
    measure: typeof product.measure === "string" ? product.measure.trim() : undefined,
    available: (() => {
      // Обработка строковых значений из Bitrix/Elasticsearch: "Y" = true, "N" = false
      if (product.available === "Y" || product.available === true) return true;
      if (product.available === "N" || product.available === false) return false;
      // Если undefined или null - по умолчанию доступен
      if (product.available === undefined || product.available === null) return true;
      // Для других значений используем Boolean
      return Boolean(product.available);
    })(),
    descriptionHtml: sanitizedDescriptionHtml,
    shortDescriptionHtml,
    imageGallery: sanitizeImageList(imageGallerySource),
    labels: sanitizeLabels(product.labels),
    variations: sanitizeVariations(product.variations),
    displayAttributes,
    allAttributes,
    recommendationSections: deriveRecommendationSections(product),
  };
};
